import React, { useEffect, useState, useRef, useCallback } from "react";

type useScrollingParams = {
    classNameOnScrollUp?: string;
    ref?: React.RefObject<Element>;
    scrollDirectionThreshold?: number;
    onScrollUpCallback?: () => void;
    onScrollDownCallback?: () => void;
};

const SCROLL_DIRECTION_THRESHOLD = 30;

export const useScrolling = (params: useScrollingParams) => {
    const {
        classNameOnScrollUp = "",
        ref,
        scrollDirectionThreshold = SCROLL_DIRECTION_THRESHOLD,
        onScrollUpCallback = () => {},
        onScrollDownCallback = () => {},
    } = params;
    const [isScrollUp, setIsScrollUp] = useState(false);
    const lastScrollY = useRef(0);

    const onScroll = useCallback(() => {
        const scrollY = window.scrollY;

        if (
            Math.abs(scrollY - lastScrollY.current) < scrollDirectionThreshold
        ) {
            return;
        }

        setIsScrollUp(scrollY < lastScrollY.current);

        lastScrollY.current = scrollY > 0 ? scrollY : 0;
    }, []);

    useEffect(() => {
        window.addEventListener("scroll", onScroll);

        return () => {
            window.removeEventListener("scroll", onScroll);
        };
    }, []);

    useEffect(() => {
        if (!ref?.current || !classNameOnScrollUp) {
            return;
        }

        if (isScrollUp) {
            ref.current.classList.add(classNameOnScrollUp);
        } else {
            ref.current.classList.remove(classNameOnScrollUp);
        }
    }, [isScrollUp]);

    useEffect(() => {
        if (isScrollUp) {
            onScrollUpCallback();
            return;
        }

        onScrollDownCallback();
    }, [isScrollUp, onScrollUpCallback, onScrollDownCallback]);
};
